import { Injectable, Output } from '@angular/core';
import { ActivatedRouteSnapshot, ActivationEnd, Event, NavigationExtras, ResolveEnd, Router, UrlTree } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { filter, take } from 'rxjs/operators';
import { SaveSheetComponent } from '../components/navigation/save-sheet/save-sheet.component';
import { getTranslationKeyForValue } from '../utils/functions/translation.functions';
import { BehaviorSubject, Observable, Subject, Subscription, Unsubscribable } from 'rxjs';
import { FooterService } from './footer.service';

type OnChangeCallback = (headerButton: any) => void;

export interface HeaderDataChanged {
  title: string;
  showBackButton: boolean;
  snapshot?: ActivatedRouteSnapshot;
}

export class NavigateBackEvent {
  public destination?: { route: string; extras?: NavigationExtras } | string;

  constructor(
    public readonly currentRoute: string,
  ) {
  }

  public get isOverridden() : boolean {
    return !!this.destination;
  }
}

@Injectable({
  providedIn: 'root'
})
export class HeaderService {
  private title: string = '';
  private titleOverride: string | null = null;

  // TODO for whole service: Look how to handle types and implement it the right way
  private static readonly DEFAULT_HEADER_BUTTON = SaveSheetComponent;

  private headerButton: any = null;
  private onChange: OnChangeCallback | null = null;
  private lastEvent: HeaderDataChanged | null = null;

  private currentPage: ActivatedRouteSnapshot | null = null;

  private navigateBackHookSubject: Subject<NavigateBackEvent> = new Subject<NavigateBackEvent>();
  private legacyHookSubscription: Subscription | null = null;

  public get navigateBackHook() : Observable<NavigateBackEvent> {
    return this.navigateBackHookSubject.asObservable();
  }

  @Output() public get onTitleChangedCallback(): Observable<HeaderDataChanged | null> {
    return this.headerDataSubject.asObservable();
  }

  private headerDataSubject = new BehaviorSubject<HeaderDataChanged | null>(null);

  constructor(
    private router: Router,
    private translate: TranslateService,
    private footer: FooterService,
  ) {

    this.resetHeaderButton();

    this.router.events.subscribe((routerEvent: Event) => {
      // We are intersted in the first ActivationEnd event that arrives after a ResolveEnd event since it will contain the right route snapshot
      if (routerEvent instanceof ResolveEnd) {
        this.router.events.pipe(
          filter(event => event instanceof ActivationEnd),
          take(1),
        ).subscribe((event: Event) => {
          const typedEvent = event as ActivationEnd;
          this.currentPage = typedEvent.snapshot;
          this.updateHeader(typedEvent.snapshot);
        })
      }
    });

    this.translate.onLangChange.subscribe(() => {
      if (this.lastEvent?.snapshot) {
        this.updateHeader(this.lastEvent.snapshot);
      }
    });

    this.footer.footerItems.pipe(filter(f => f?.length > 0)).subscribe(() => {
      if (this.lastEvent?.snapshot) {
        this.updateHeader(this.lastEvent.snapshot);
      }
    });
  }

  public initialize(onChange: OnChangeCallback): void {
    this.setOnChangeCallback(onChange);
  }

  public setTitleOverride(title: string | null): void {
    this.titleOverride = title;
    if (this.lastEvent?.snapshot) {
      this.updateHeader(this.lastEvent.snapshot);
    }
  }

  public refreshHeaderTitle(snapshot: ActivatedRouteSnapshot): void {
    this.updateHeader(snapshot);
  }

  public setOnChangeCallback(onChange: OnChangeCallback): void {
    this.onChange = onChange;
    if (this.headerButton) {
      this.onChange(this.headerButton);
    }
  }

  public getHeaderButton(): any {
    return this.headerButton;
  }

  public setHeaderButton(headerButton: any): void {
    this.changeButton(headerButton);
  }

  public resetHeaderButton(): void {
    this.changeButton(HeaderService.DEFAULT_HEADER_BUTTON);
  }

  public setNavigateBackHook(hook: (currentRoute: string) => { route: string; extras?: NavigationExtras } | string | null): void {
    if (this.legacyHookSubscription) {
      this.legacyHookSubscription.unsubscribe();
      this.legacyHookSubscription = null;
    }

    if (hook) {
      this.legacyHookSubscription = this.navigateBackHookSubject.subscribe((evt) => {
        const newPath = hook(evt.currentRoute);
        if (newPath) {
          evt.destination = newPath;
        }
      });
    }
  }

  public navigateBack(): void {
    if (!this.currentPage) {
      console.error('Current page not set, cannot navigate back');
      return;
    }

    try {
      if (this.navigateBackHookSubject.observed) {
        const result = this.getHookedBackNavigationPath();
        if (result) {
          if (typeof result === "string") {
            this.router.navigate([result]);
          }
          else {
            this.router.navigate([result.route], result.extras);
          }
          // Do not continue with normal back navigation
          return;
        }
      }

      // Note: This isn't set up for multiple route nesting levels, but we don't use those in DEVEX at the moment
      const parentPath: string = this.currentPage.data['parent'];
      const parentFromRoot: boolean = this.currentPage.data['parentFromRoot'] ?? false;

      if (!parentPath) {
        // Try to fall back to a basic parent
        this.router.navigate([this.currentPage.parent?.routeConfig?.path]);
        return;
      }

      // Resolve variables within parentPath using our current path
      let resolvedPath: string = parentPath.replace(/:([\w-]+)/g, (param: string) => {
        param = param.substring(1);
        return this.currentPage!.paramMap.get(param) ?? '';
      });

      // Deal with queryparams
      const urlTree: UrlTree = this.router.parseUrl(resolvedPath);
      // Get rid of query params in url, we pass them to navigate separately
      if (urlTree.queryParamMap.keys.length > 0) {
        resolvedPath = resolvedPath.substring(0, resolvedPath.indexOf('?'))
      }

      if (!parentFromRoot) {
        resolvedPath = this.currentPage.parent?.url[0].path + '/' + resolvedPath;
      }

      this.router.navigate([resolvedPath], { queryParams: urlTree.queryParams });
    }
    catch (error) {
      console.error('Error on back button click', error);

      // Try to fall back to a basic parent
      this.router.navigate([this.currentPage.parent?.routeConfig?.path]);
    }
  }

  private getHookedBackNavigationPath(): string | {
    route: string;
    extras?: NavigationExtras;
  } | null {
    if (this.navigateBackHookSubject.observed && this.currentPage) {
      const currentPath = this.extractPath(this.currentPage);
      let navEvent = new NavigateBackEvent(currentPath);
      this.navigateBackHookSubject.next(navEvent);
      return navEvent.destination ?? null;
    }
    return null;
  }

  private extractPath(route: ActivatedRouteSnapshot): string {
    let path = '';
    for (let i = 1; i < route.pathFromRoot?.length; i++) {
      try {
        const segment: string = route.pathFromRoot[i].url[0].path;
        if (segment.length > 0) {
          if (path.length > 0) {
            path += '/';
          }
          path += segment;
        }
      }
      catch {
      }
    }
    return path;
  }

  private changeButton(button: any): void {
    this.headerButton = button;
    if (this.onChange) {
      this.onChange(this.headerButton);
    }
  }

  private updateHeader(snapshot: ActivatedRouteSnapshot): void {
    if (!snapshot) {
      return;
    }

    let title: string = snapshot.data['breadcrumb'];
    let showBackButton: boolean = snapshot.data['showbackbutton'];

    // If route is in a child component get the breadcrumb from the first so we can set things like id and name
    if (snapshot.children.length > 0) {
      title = snapshot.children[0].data['breadcrumb'];
      showBackButton = snapshot.children[0].data['showbackbutton'];
    }

    const currentPath: string = this.extractPath(snapshot);

    // Check if the back button needs to be removed if this route is a direct footer item
    this.footer.footerItems.pipe(take(1)).subscribe((footerItems) => {
      if (footerItems.find(f => f.route === currentPath)) {
        showBackButton = false;
      }
    });

    // Check if the navigate back hook requires us to create a back button
    if (!showBackButton) {
      if (this.getHookedBackNavigationPath()) {
        showBackButton = true;
      }
    }

    const translationKey: string = getTranslationKeyForValue(title ?? '');
    this.title = translationKey.length > 0 ? this.translate.instant(`Header.Title.${translationKey}`) : '';

    this.lastEvent = { title: this.titleOverride ?? this.title, showBackButton: !!showBackButton, snapshot: snapshot };
    this.headerDataSubject.next(this.lastEvent);
  }
}
